import type { ReactNode } from "react";
import { Button } from "@charisol-spark/ui";
import {
  ArrowRight,
  CheckCircle as CheckCircle2,
  CaretRight as ChevronRight,
} from "@phosphor-icons/react";
import {
  CalendarCheck,
  Eye,
  Globe,
  Lightning,
  MagicWand,
  MicrophoneStage,
  Palette,
  Repeat,
  ShieldCheck,
  Sparkle,
  SquaresFour,
  Target,
  UsersThree,
} from "@phosphor-icons/react";
import { useSiteUser } from "./session";
import { useTheme } from "@charisol-spark/ui";
import { LandingNav } from "./LandingNav";
import { LandingFooter } from "./LandingFooter";
// Images go through vite-imagetools: WebP at the widths these actually
// render at. The PNG sources are 2-6 MB each and were being shipped whole.
import dashboardImageDark from "./assets/dark-dashboard.png?format=webp&w=1280";
import dashboardImageDarkSrcSet from "./assets/dark-dashboard.png?format=webp&w=768;1280;2048&as=srcset";
import dashboardImageLight from "./assets/light-dashboard.png?format=webp&w=1280";
import dashboardImageLightSrcSet from "./assets/light-dashboard.png?format=webp&w=768;1280;2048&as=srcset";
import heroBg from "./assets/hero-bg-animated.jpeg?format=webp&w=1920";
import zayzayAvatar from "./assets/zayzay-avatar.png?format=webp&w=96";
import useCase1Bg from "./assets/use-case-1.png?format=webp&w=800";
import useCase2Bg from "./assets/use-case-2.png?format=webp&w=800";
import useCase3Bg from "./assets/use-case-3.png?format=webp&w=800";
import { hardNavigate } from "./bundleBoundary";

/** Where "Contact Sales" goes until Spark has its own sales page. */
const CONTACT_SALES_URL = "https://charisol.io/get-started/";

/* ------------------------------------------------------------------ */
/*  Platform logos                                                     */
/* ------------------------------------------------------------------ */

// Only LinkedIn and X can be published to today. The others are on the
// roadmap and are shown with a "Soon" label; see docs/product/PRODUCT_ROADMAP.md.
const platformLogos: { name: string; soon?: boolean; logo: ReactNode }[] = [
  {
    name: "LinkedIn",
    logo: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: "X / Twitter",
    logo: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    name: "Instagram",
    soon: true,
    logo: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  {
    name: "Facebook",
    soon: true,
    logo: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    name: "Threads",
    soon: true,
    logo: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.855-.71 2.024-1.168 3.476-1.363.908-.122 1.866-.128 2.856-.018a6.18 6.18 0 00-.025-.478c-.133-1.407-.678-2.127-1.62-2.14-.805-.01-1.394.28-1.75.861l-1.848-1.1c.718-1.2 1.98-1.863 3.55-1.863h.06c1.312.012 2.373.457 3.156 1.322.826.913 1.263 2.2 1.299 3.828.015.678-.01 1.387-.072 2.109.958.455 1.776 1.088 2.394 1.882.813 1.045 1.228 2.322 1.201 3.696-.033 1.665-.633 3.335-1.738 4.838C18.656 22.643 15.912 23.975 12.186 24zM9.93 14.407c-.59.49-.827 1.073-.793 1.683.033.584.321 1.084.812 1.408.535.352 1.257.521 2.034.476.956-.056 1.723-.401 2.28-1.025.487-.545.835-1.323.987-2.308-.806-.107-1.593-.126-2.363-.056-.964.088-1.77.354-2.331.765-.146.109-.284.231-.413.365l-.213-.308z" />
      </svg>
    ),
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * Public landing / marketing page rendered at `/`.
 *
 * If the user is already authenticated it shows a "Go to Dashboard"
 * button; otherwise it directs to Sign Up / Sign In.
 */
export function LandingPage() {
  const user = useSiteUser();
  const { resolvedTheme } = useTheme();

  /** Primary CTA destination based on auth state. */
  const handleGetStarted = () => {
    hardNavigate(user ? "/app" : "/auth/register");
  };

  /* ── Data ─────────────────────────────────────────────────── */

  const features = [
    {
      icon: <Repeat size={24} weight="duotone" />,
      title: "Start From What You Already Have",
      description:
        "An idea, a note, a Medium article, pasted text, or a Word, PDF or text file. Spark writes the first draft, so you never face a blank page.",
    },
    {
      icon: <SquaresFour size={24} weight="duotone" />,
      title: "Shaped for LinkedIn and X",
      description:
        "Spark formats each post for the network it's going to, so it fits the length and style people expect there. You can build slide-style carousels too.",
    },
    {
      icon: <MicrophoneStage size={24} weight="duotone" />,
      title: "Sounds Like You",
      description:
        "Tell Spark how you write once and it saves your voice as a Persona. Every draft sounds like you, not like a machine. Keep more than one for different sides of your work.",
    },
    {
      icon: <CalendarCheck size={24} weight="duotone" />,
      title: "Publish or Schedule",
      description:
        "Post straight to LinkedIn and X, or pick a time on the calendar. No copying and pasting between tabs.",
    },
    {
      icon: <Target size={24} weight="duotone" />,
      title: "Research and Fact-Check",
      description:
        "Ask Spark to look into a topic or check the facts in a draft. It shows the sources it used, and you can save what it finds for later.",
    },
    {
      icon: <Palette size={24} weight="duotone" />,
      title: "Keep Showing Up",
      description:
        "Posting streaks, badges and campaigns make it easier to share regularly, not just when inspiration strikes.",
    },
  ];

  // The creator and pieces figures are placeholder social proof, not measured
  // numbers; replace them with real, current figures as soon as they exist
  // (docs/product/BRAND_IDENTITY.md, "Things to check").
  const stats = [
    { value: "2,500+", label: "Creators" },
    { value: "50K+", label: "Pieces Repurposed" },
    { value: "LinkedIn + X", label: "Publish and schedule directly" },
    { value: "Your voice", label: "Saved as a Persona" },
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Bring Your Idea",
      description:
        "Start from a saved idea, paste text or a Medium link, or upload a Word, PDF or text file. Whatever you know, start there.",
      icon: <Lightning size={32} weight="duotone" />,
    },
    {
      step: "02",
      title: "Get a Draft in Your Voice",
      description:
        "Choose LinkedIn or X and Spark writes a post that sounds like you, using the Persona you set up.",
      icon: <Globe size={32} weight="duotone" />,
    },
    {
      step: "03",
      title: "Polish and Share",
      description:
        "Edit it until it says what you mean. Spark keeps every version. Then publish now or schedule it for later.",
      icon: <Sparkle size={32} weight="duotone" />,
    },
  ];

  const workflowSteps = [
    {
      icon: <Lightning size={24} weight="duotone" />,
      title: "Save Ideas as They Come",
      description:
        "Jot down ideas when they come to you, and write them into posts when you're ready.",
    },
    {
      icon: <MagicWand size={24} weight="duotone" />,
      title: "Plan a Campaign",
      description:
        "Group related posts into a campaign and decide when each one goes out.",
    },
    {
      icon: <CalendarCheck size={24} weight="duotone" />,
      title: "Schedule Ahead",
      description:
        "Line up posts on the calendar for LinkedIn and X, so you keep showing up during busy weeks.",
    },
  ];

  const useCases = [
    {
      title: "Consultants and Founders",
      description:
        "Share what you've learned from your work, be known for it, and win work because of it.",
      image: useCase1Bg,
    },
    {
      title: "Experts and Creators",
      description:
        "You have plenty to say. Spark gets it out of your head and onto LinkedIn and X without it becoming a second job.",
      image: useCase2Bg,
    },
    {
      title: "Teams and Agencies",
      description:
        "Each person can use Spark in their own voice today. Shared team spaces and reviews are coming soon.",
      image: useCase3Bg,
    },
  ];

  /* ── Render ───────────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-background text-foreground font-sans antialiased">
      {/* ========== HEADER ========== */}
      <LandingNav />

      {/* ========== HERO SECTION ========== */}
      <section className="relative flex flex-col overflow-hidden">
        {/* Row 1: Hero background */}
        <div className="relative h-[100vh] overflow-hidden">
          <img
            src={heroBg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-[#2D274B]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-12 md:h-48 bg-gradient-to-t from-background to-transparent" />
        </div>

        {/* Row 2 */}
        <div className="relative h-[30vh] bg-background" />

        {/* Hero content */}
        <div className="absolute inset-0 z-10 flex flex-col justify-center pt-24 sm:pt-32 md:pt-48">
          <div className="max-w-[1280px] mx-auto px-6 md:px-8 pt-28 sm:pt-40 md:pt-72 pb-16">
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-[#9787F3] text-sm font-medium mb-8 animate-fade-in border border-primary/20">
                <MagicWand className="w-4 h-4" />
                Share your perspective
              </div>

              {/* Headline */}
              <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] font-bold leading-[1.05] tracking-[-0.03em] text-white mb-2 animate-fade-in-delay-1">
                Less friction.
              </h1>
              <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] font-bold leading-[1.05] tracking-[-0.03em] mb-6 animate-fade-in-delay-1">
                <span className="bg-gradient-to-r from-[#9787F3] to-[#EAEFFE] bg-clip-text text-transparent">
                  More you.
                </span>
              </h1>

              {/* Sub */}
              <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-delay-2">
                Your voice deserves to be heard. Spark turns your experiences,
                opinions and ideas into posts that sound like you, then helps
                you publish them on LinkedIn and X and keep showing up.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-3">
                <Button
                  variant="white"
                  size="xl"
                  pill
                  onClick={handleGetStarted}
                >
                  {user ? "Go to Dashboard" : "Get started"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                {/* The hero sits on a fixed dark overlay in both themes, so the
                    label stays white on hover too — without hover:text-white the
                    outline variant's own hover:text-accent-foreground takes over
                    and puts purple on near-black. */}
                {!user && (
                  <Button
                    variant="outline"
                    size="lg"
                    pill
                    onClick={() => hardNavigate("/auth/login")}
                    className="border-white/30 text-white hover:bg-white/10 hover:text-white"
                  >
                    Sign In
                  </Button>
                )}
              </div>

              {/* Social proof */}
              <p className="text-sm text-white/50 mt-4 animate-fade-in-delay-3">
                Join 2,500+ creators already using Spark
              </p>
            </div>

            {/* Dashboard Preview */}
            <div className="relative mt-16 md:mt-24 max-w-5xl mx-auto animate-fade-in-delay-5">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#2D274B]/10 dark:shadow-primary/5 border-[5px] border-border/50">
                <img
                  src={
                    resolvedTheme === "dark"
                      ? dashboardImageDark
                      : dashboardImageLight
                  }
                  srcSet={
                    resolvedTheme === "dark"
                      ? dashboardImageDarkSrcSet
                      : dashboardImageLightSrcSet
                  }
                  sizes="(min-width: 1024px) 1024px, 100vw"
                  alt="The Spark dashboard"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 z-20 left-0 right-0 h-48 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* ========== STATS BAR ========== */}
      <section className="py-16 md:py-20 border-b border-border/30">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary dark:text-[#9787F3] mb-2">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SUPPORTED PLATFORMS ========== */}
      <section className="py-12 md:py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <p className="text-center text-sm text-muted-foreground mb-8 font-medium">
            Publish to LinkedIn and X today. More networks are coming soon.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {platformLogos.map((platform, i) => (
              <div
                key={i}
                className={`flex items-center gap-2.5 transition-colors group ${
                  platform.soon
                    ? "text-muted-foreground/60"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span className="opacity-80 group-hover:opacity-100 transition-opacity">
                  {platform.logo}
                </span>
                <span className="text-sm font-medium hidden sm:inline">
                  {platform.name}
                </span>
                {platform.soon && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full border border-border px-1.5 py-0.5">
                    Soon
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section
        id="features"
        className="py-24 md:py-32 relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-background to-transparent pointer-events-none z-10" />

        {/* Background pattern */}
        <div className="absolute inset-0 pointer-events-none">
          <svg
            className="absolute top-0 left-0 w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="features-grid"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="20" cy="20" r="1.5" className="fill-primary/35" />
              </pattern>
              <pattern
                id="features-grid-dark"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="20" cy="20" r="1" className="fill-primary/25" />
              </pattern>
            </defs>
            <rect
              className="dark:hidden"
              width="100%"
              height="100%"
              fill="url(#features-grid)"
            />
            <rect
              className="hidden dark:block"
              width="100%"
              height="100%"
              fill="url(#features-grid-dark)"
            />
          </svg>
          <div className="absolute top-20 left-10 w-[600px] h-[600px] bg-primary/30 dark:bg-primary/15 rounded-full blur-[150px]" />
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-primary/25 dark:bg-primary/10 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
            <p className="text-xs font-bold text-primary uppercase tracking-[0.2em] mb-4">
              Features
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-4">
              Everything you need to share your perspective
            </h2>
            <p className="text-lg text-muted-foreground">
              From first idea to published post, in your own voice, with less
              effort at every step.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-card/50 dark:bg-card/30 border border-border/50 hover:border-primary/30 hover:bg-card/80 dark:hover:bg-card/50 transition-all duration-500 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5 group-hover:scale-110 transition-transform duration-500">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 tracking-tight">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-muted/30 dark:from-[#2D274B]/50 to-transparent pointer-events-none" />
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section
        id="how-it-works"
        className="py-24 md:py-32 bg-muted/30 dark:bg-[#2D274B]/20"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
            <p className="text-xs font-bold text-primary uppercase tracking-[0.2em] mb-4">
              How It Works
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-4">
              From Idea to Post in 3 Steps
            </h2>
            <p className="text-lg text-muted-foreground">
              Start with a thought, an experience or an opinion. Finish with a
              post you&rsquo;re happy to put your name on.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative">
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-primary/30 to-transparent" />
                )}
                <div className="text-center md:text-left">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-6">
                    {step.icon}
                  </div>
                  <div className="text-xs font-bold text-primary uppercase tracking-[0.2em] mb-2">
                    Step {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== INSPIRATION ENGINE ========== */}
      <section
        id="inspiration"
        className="py-24 md:py-32 relative overflow-hidden"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-xs font-bold text-primary uppercase tracking-[0.2em] mb-4">
                Start From Anywhere
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-6">
                Never Start From Scratch 😉
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Read something that got you thinking? Wrote an article nobody
                saw? Bring it to Spark and get a first draft of your own take,
                in your voice, ready to shape and share.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Paste a Medium link or text, or upload a file",
                  "Research a topic and check the facts",
                  "Get a first draft in your voice",
                  "Publish or schedule to LinkedIn and X",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handleGetStarted}
                className="group relative px-7 py-3 rounded-full text-sm font-medium transition-all duration-500 overflow-hidden
                  bg-gradient-to-b from-primary to-primary/90 dark:from-white/20 dark:to-[#2D274B]/70
                  backdrop-blur-xl border border-white/50 dark:border-white/10
                  shadow-[0_2px_20px_-4px_hsl(var(--primary)/0.5),inset_0_1px_1px_rgba(255,255,255,0.6)]
                  dark:shadow-[0_2px_20px_-4px_hsl(var(--primary)/0.3),inset_0_1px_1px_rgba(255,255,255,0.1)]
                  hover:shadow-[0_4px_30px_-4px_hsl(var(--primary)/0.6),inset_0_1px_1px_rgba(255,255,255,0.8)]
                  hover:scale-[1.02] active:scale-[0.98]
                  text-primary-foreground dark:text-white"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Try Spark
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
              </button>
            </div>

            {/* Visual */}
            <div className="relative">
              <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MagicWand
                      size={20}
                      weight="duotone"
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-sm">New draft</p>
                    <p className="text-xs text-muted-foreground">
                      From a link, text or a file
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-4 border border-dashed border-border">
                    <p className="text-sm text-muted-foreground">
                      🔗 Reading your article...
                    </p>
                  </div>
                  <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                    <p className="text-sm font-medium mb-2">
                      ✨ Your draft is ready!
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Written in your voice, from your own ideas. Edit it, then
                      publish or schedule it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== COLLABORATION ========== */}
      <section className="py-24 md:py-32 bg-muted/30 dark:bg-[#2D274B]/20">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Visual */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={zayzayAvatar}
                    alt="AI Assistant"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-sm">ZayZay</p>
                    <p className="text-xs text-muted-foreground">
                      Your AI content partner
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      Reviewing your LinkedIn post before publishing...
                    </p>
                  </div>
                  <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                    <p className="text-sm">
                      💡 Strong opening. Maybe end with a question to invite
                      replies?
                    </p>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <div className="flex -space-x-2">
                      <div className="w-6 h-6 rounded-full bg-primary/20 border-2 border-card flex items-center justify-center text-xs">
                        J
                      </div>
                      <div className="w-6 h-6 rounded-full bg-accent border-2 border-card flex items-center justify-center text-xs">
                        M
                      </div>
                      <div className="w-6 h-6 rounded-full bg-muted border-2 border-card flex items-center justify-center text-xs">
                        +2
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      4 team members reviewing
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <p className="text-xs font-bold text-primary uppercase tracking-[0.2em]">
                  Collaboration
                </p>
                <ComingSoonBadge />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-6">
                Get Feedback Before You Ship
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Coming soon: invite teammates, clients or stakeholders to review
                posts before they go live, with comments and approvals in one
                place. For now, each person can use Spark in their own voice.
              </p>

              <div className="space-y-4">
                {[
                  "Shared team spaces",
                  "A review and approval step",
                  "One shared voice for the whole team",
                  "Shared libraries of ideas and material",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== ENTERPRISE ========== */}
      <section
        id="enterprise"
        className="py-24 md:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <svg
            className="absolute top-0 left-0 w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="enterprise-grid"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 60 0 L 0 0 0 60"
                  fill="none"
                  className="stroke-primary/10"
                  strokeWidth="1"
                />
              </pattern>
              <pattern
                id="enterprise-grid-dark"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 60 0 L 0 0 0 60"
                  fill="none"
                  className="stroke-primary/15"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect
              className="dark:hidden"
              width="100%"
              height="100%"
              fill="url(#enterprise-grid)"
            />
            <rect
              className="hidden dark:block"
              width="100%"
              height="100%"
              fill="url(#enterprise-grid-dark)"
            />
          </svg>
          <div className="absolute top-10 right-20 w-[500px] h-[500px] bg-primary/20 dark:bg-primary/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-10 left-20 w-[400px] h-[400px] bg-primary/15 dark:bg-primary/8 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <p className="text-xs font-bold text-primary uppercase tracking-[0.2em]">
                Teams &amp; Enterprise
              </p>
              <ComingSoonBadge />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-4">
              Share Your Team&rsquo;s Perspective
            </h2>
            <p className="text-lg text-muted-foreground">
              Team and company features are coming soon. Until then, everyone on
              your team can use Spark with their own account and voice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              {
                icon: <UsersThree size={24} weight="duotone" />,
                title: "Team Spaces",
                description: "Shared spaces with members and roles",
              },
              {
                icon: <Eye size={24} weight="duotone" />,
                title: "Shared Voice",
                description:
                  "One voice and set of writing rules across the team",
              },
              {
                icon: <CheckCircle2 size={24} weight="duotone" />,
                title: "Review Before Posting",
                description: "Submit, review, approve or send back",
              },
              {
                icon: <ShieldCheck size={24} weight="duotone" />,
                title: "Company Sign-In",
                description: "Log in with your company's own account system",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-card/50 dark:bg-card/30 border border-border/30 hover:border-primary/30 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              variant="outline"
              size="lg"
              pill
              className="border-primary/30 hover:bg-primary/10"
              onClick={() => hardNavigate(CONTACT_SALES_URL)}
            >
              Contact Sales
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ========== WORKFLOW AUTOMATION ========== */}
      <section
        id="workflow"
        className="py-24 md:py-32 bg-muted/30 dark:bg-[#2D274B]/20"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6 border border-primary/20">
              <Repeat size={14} weight="bold" />
              PLAN AHEAD
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.02em] mb-6">
              Keep showing up, even in busy weeks
            </h2>
            <p className="text-lg text-muted-foreground">
              Save ideas as they come, group posts into campaigns and schedule
              them ahead, so sharing your perspective becomes a habit, not a
              scramble.
            </p>
          </div>

          <div className="relative bg-card/50 dark:bg-[#1a1730]/80 rounded-3xl border border-border/30 p-8 md:p-12 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 pointer-events-none" />

            <div className="relative grid md:grid-cols-3 gap-8 md:gap-6 items-start">
              {workflowSteps.map((step, index) => (
                <div key={index} className="relative">
                  {index < workflowSteps.length - 1 && (
                    <div className="hidden md:flex absolute top-8 -right-3 items-center z-10">
                      <div className="flex items-center gap-1 text-primary/40">
                        <span className="text-lg">- - -</span>
                        <span className="text-lg">- - →</span>
                      </div>
                    </div>
                  )}

                  <div className="text-center md:text-left">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary mb-5 border border-primary/20">
                      {step.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center justify-center md:justify-start gap-2">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== USE CASES ========== */}
      <section id="use-cases" className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs font-bold text-primary uppercase tracking-[0.2em] mb-4">
              Use Cases
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-4">
              Built for People With Something to Say
            </h2>
            <p className="text-lg text-muted-foreground">
              Whether you&rsquo;re building an audience on your own or with a
              team, Spark makes it easier to share your perspective.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl border border-border/30 hover:border-primary/30 transition-all duration-500 h-[280px]"
              >
                <img
                  src={useCase.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D274B] via-[#2D274B]/50 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-8">
                  <h3 className="text-xl font-semibold mb-3 text-white flex items-center gap-2">
                    {useCase.title}
                    <ChevronRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                  </h3>
                  <p className="text-white/80 mb-4">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <svg
            className="absolute top-0 left-0 w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="cta-grid"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 60 0 L 0 0 0 60"
                  fill="none"
                  className="stroke-primary/15"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
          <div className="absolute top-10 right-20 w-[500px] h-[500px] bg-primary/20 dark:bg-primary/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-10 left-20 w-[400px] h-[400px] bg-primary/15 dark:bg-primary/8 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 relative z-10">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#2D274B] to-[#1a1730] dark:from-primary/20 dark:to-[#2D274B]/50 p-12 md:p-20 text-center">
            <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-primary/20 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-white/5 rounded-full blur-[80px]" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.02em] text-white dark:text-foreground mb-6">
                Ready to Share Your Perspective?
              </h2>
              <p className="text-lg text-white/70 dark:text-muted-foreground max-w-xl mx-auto mb-10">
                Join thousands of creators who stopped starting from scratch.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="white"
                  size="lg"
                  pill
                  onClick={handleGetStarted}
                >
                  {user ? "Go to Dashboard" : "Try Spark"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  pill
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white dark:border-border dark:text-foreground dark:hover:bg-muted/50 dark:hover:text-accent-foreground"
                  onClick={() => hardNavigate(CONTACT_SALES_URL)}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </section>

      {/* ========== FOOTER ========== */}
      <LandingFooter />
    </div>
  );
}

/** Small pill marking a section that describes planned, not-yet-built features. */
function ComingSoonBadge() {
  return (
    <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
      Coming soon
    </span>
  );
}
